import { useState } from 'react';
import styled from '@emotion/styled';
import { getEnvValue } from '@myme/utils';
import { useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'src/hooks';
import { MenuItem, TextField } from 'src/components/atoms';
import { FlexColumn } from 'src/components/layout';
import { Dialog } from 'src/components/organisms';
import { useContentContext } from 'src/components/pages/mymes/editor/contentStep/ContentContext';
import { ErrorLocationMessage } from './ErrorLocationMessage';
import { Map } from './Map';
import { mapContainerStyles, MapSkeleton } from './MapSkeleton';
import { useMapLogic } from './useMapLogic';

// Without this index search doesn't propose locations after typing letters
const StyledDialog = styled(Dialog)`
  z-index: 1000;
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: grid;
  align-items: center;
`;

interface Props {
  onClose: () => void;
  blobId: string;
  // it is optional due to that blobs in blobs library don't have module
  moduleId?: string;
  closeDialog: () => void;
}

type Library = 'places' | 'drawing' | 'geometry' | 'localContext' | 'visualization';
const libraries: Library[] = ['places'];

const googleMapsApiKey = getEnvValue('REACT_APP_GOOGLE_MAPS_API_KEY');

export const MapDialog = ({ onClose, blobId, moduleId, closeDialog }: Props) => {
  const { t } = useTranslation();

  const { setModules, setBlobsWithoutSetLocation, setAreModulesEdited } = useContentContext();

  const { marker, radius, radiusOptions, handleRadiusChange, setMarker, setMap, ...map } = useMapLogic({
    blobId,
    moduleId
  });

  const [noLocationError, setNoLocationError] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries
  });

  const setLocation = (blobId: string, moduleId: string) => {
    if (marker?.lat()) {
      setModules(prevState => {
        const updatedState = [...prevState];
        const moduleIndex = updatedState.findIndex(currentModule => currentModule.id === moduleId);
        const blobIndex = updatedState[moduleIndex].blobs.findIndex(currentBlob => currentBlob.id === blobId);
        updatedState[moduleIndex].blobs[blobIndex] = {
          ...updatedState[moduleIndex].blobs[blobIndex],
          location: { latitude: marker?.lat(), longitude: marker?.lng(), radiusInMeters: radius }
        };
        return updatedState;
      });

      setBlobsWithoutSetLocation(prevState => {
        const updatedState = [...prevState];
        const currentBlobIndex = updatedState.findIndex(
          currentBlobWithError => currentBlobWithError.blobId === blobId && currentBlobWithError.moduleId === moduleId
        );
        updatedState.splice(currentBlobIndex, 1);

        return updatedState;
      });
      closeDialog();
      setNoLocationError(false);
      setAreModulesEdited(true);
      setMarker(null);
      setMap(null);
    }
    setNoLocationError(true);
  };

  return (
    <StyledDialog
      title={t('pages.mymes.editor.blobs.mapDialog.title_header')}
      onClose={onClose}
      defaultActionButtons={{
        action: () => moduleId && setLocation(blobId, moduleId),
        btnConfirmText: moduleId && t('pages.mymes.editor.blobs.mapDialog.save')
      }}
      enableBackdropClick
      enableEscapeKeyDown
    >
      {isLoaded ? (
        <Container>
          <FlexColumn
            marginX='auto'
            justifyContent='center'
            width={mapContainerStyles.width}
            maxWidth={mapContainerStyles.maxWidth}
            mt={-2}
          >
            <Map marker={marker} radius={radius} {...map} />
            {noLocationError && <ErrorLocationMessage />}
            <TextField
              select
              size='small'
              value={radius}
              placeholder={t('pages.mymes.editor.blobs.mapDialog.map_radius_field')}
              onChange={handleRadiusChange}
            >
              {radiusOptions.map(singleValue => (
                <MenuItem key={singleValue.value} value={singleValue.value}>
                  {singleValue.text}
                </MenuItem>
              ))}
            </TextField>
          </FlexColumn>
        </Container>
      ) : (
        <MapSkeleton />
      )}
    </StyledDialog>
  );
};
